import { useCopilotSessionContext } from '../components/session-provider';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import { generatePath } from '@/services/router/generate-path';
import { useWorkspaceIdFromCurrentUrl } from '@/services/router/use-workspace-id';
import { buildAiFrom } from '../utils/build-ai-from';
import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';

const AGENT_CHAT_PATTERN = /\/workspaces\/\d+\/agent\/?$/;

/**
 * Returns a function that navigates to the agent chat page.
 * Optionally resets the session and sends a message.
 *
 * Skips navigation when already on the agent chat page to
 * avoid pushing duplicate entries onto the browser history stack.
 */
export function useNavigateToAgentChat() {
  const session = useCopilotSessionContext();
  const navigate = useSafeNavigate();
  const workspaceId = useWorkspaceIdFromCurrentUrl() ?? 0;
  const { pathname } = useLocation();

  return useCallback(
    (options?: { message?: string; sessionId?: string }) => {
      const newSession = session.reset();

      if (options?.sessionId != null) {
        void newSession.initialize(options.sessionId);
      } else if (options?.message != null) {
        void newSession.sendMessage(options.message);
      }

      if (!AGENT_CHAT_PATTERN.test(pathname)) {
        navigate(generatePath('/workspaces/:workspaceId/agent', { workspaceId }, { 'ai-from': buildAiFrom() }));
      }
    },
    [session, navigate, workspaceId, pathname],
  );
}
